import React, { useState, useEffect, useContext } from 'react';
import { Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEmailConfig } from './EmailConfigContext';
import { UserContext } from '../../../../../context/UserContext';
import './index.css';

function EmailAutomation() {
    const location = useLocation();
    const navigate = useNavigate();
    const { schedule, parameters } = useEmailConfig();
    const { user } = useContext(UserContext);
    const [systemStatus, setSystemStatus] = useState('Not configured');
    const [nextExecution, setNextExecution] = useState('Not set');
    const [lastRun, setLastRun] = useState('No records found');
    const [executionDays, setExecutionDays] = useState('Not set');
    const [timesPerDay, setTimesPerDay] = useState('0');
    const [workingHours, setWorkingHours] = useState('0');
    const [interval, setIntervalValue] = useState('Not set');
    const [userEmail, setUserEmail] = useState('');
    const [selectedEmail, setSelectedEmail] = useState(() => localStorage.getItem('emailAutomation_selectedEmail') || '');
    const [availableEmails, setAvailableEmails] = useState([]);
    const [aiClassification, setAiClassification] = useState('Loading...');
    const [aiReplyDetection, setAiReplyDetection] = useState('Loading...');
    const [aiReplyGeneration, setAiReplyGeneration] = useState('Loading...');
    const [scheduleData, setScheduleData] = useState(null); // For storing fetched schedule data
    const [parametersData, setParametersData] = useState(null); // For storing fetched parameters data
    const [statusBar, setStatusBar] = useState({ show: false, message: '', isSuccess: true });
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSystemStatusHelpOpen, setIsSystemStatusHelpOpen] = useState(false);
    const [isConfigurationSummaryHelpOpen, setIsConfigurationSummaryHelpOpen] = useState(false);
    const [isEmailProcessingHelpOpen, setIsEmailProcessingHelpOpen] = useState(false);

    const getStoredEmails = () => {
        try {
            const savedEmails = JSON.parse(localStorage.getItem('emailAutomation_userEmails') || '[]');

            return Array.isArray(savedEmails)
                ? savedEmails.map(email => email.trim()).filter(Boolean)
                : [];
        } catch (error) {
            console.error('Error reading stored email configuration:', error);
            return [];
        }
    };

    const getConfiguredEmails = () => {
        const contextEmails = parameters.userEmail
            ? parameters.userEmail.split(',').map(email => email.trim()).filter(Boolean)
            : [];

        return contextEmails.length > 0 ? contextEmails : getStoredEmails();
    };

    // Fetch schedule data from the API
    const fetchScheduleData = async (email) => {
        if (!email) return;

        try {
            // Prepare headers with authorization
            const headers = {
                'Content-Type': 'application/json',
            };
            
            // Add authorization header if user is available
            if (user && user.tokenType && user.accessToken) {
                headers['Authorization'] = `${user.tokenType}${user.accessToken}`;
            }

            const response = await fetch(`/server/rest/ai-agent/schedule/${email}`, {
                method: 'GET',
                headers: headers,
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Parse the schedule data from the response
                let scheduleData;
                try {
                    scheduleData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
                } catch (parseError) {
                    console.error('Error parsing schedule data:', parseError);
                    return;
                }
                
                // Store the fetched schedule data in local state
                setScheduleData(scheduleData);
            }
        } catch (err) {
            console.error('Error fetching schedule data:', err);
        }
    };

    // Fetch parameters data from the API
    const fetchParametersData = async (email) => {
        if (!email) return;

        try {
            // Prepare headers with authorization
            const headers = {
                'Content-Type': 'application/json',
            };
            
            // Add authorization header if user is available
            if (user && user.tokenType && user.accessToken) {
                headers['Authorization'] = `${user.tokenType}${user.accessToken}`;
            }

            const response = await fetch(`/server/rest/ai-agent/parameters/${email}`, {
                method: 'GET',
                headers: headers,
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Parse the parameters data from the response
                let paramsData;
                try {
                    paramsData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
                } catch (parseError) {
                    console.error('Error parsing parameters data:', parseError);
                    return;
                }
                
                // Store the fetched parameters data in local state
                setParametersData(paramsData);
            }
        } catch (err) {
            console.error('Error fetching parameters data:', err);
        }
    };

    // Mock functions to simulate external JS functionality
    const shouldRunNow = (schedule) => {
        // Handle case where schedule is undefined or null
        if (!schedule) {
            return false;
        }
        // Simple mock logic - randomly decide if should run
        return Math.random() > 0.5;
    };

    const getScheduleSummary = (schedule) => {
        // Handle case where schedule is undefined or null
        if (!schedule) {
            return {
                executionDays: '',
                timesPerDay: '',
                startTime: '',
                endTime: '',
                interval: '',
                nextExecution: '',
                lastRun: 'Never'
            };
        }
        
        // Handle API response structure
        // API returns: executionDays, workingHours, timesPerDay, interval, nextExecution, timezone, lastRun
        if (schedule.executionDays) {
            // Adjust nextExecution time by subtracting 4 hours for proper timezone display
            let adjustedNextExecution = schedule.nextExecution || 'Loading...';
            if (schedule.nextExecution && schedule.nextExecution !== 'Loading...') {
                try {
                    // Parse the date string (assuming format: "Friday, January 23, 2026 at 1:00 PM")
                    const dateParts = schedule.nextExecution.split(' ');
                    if (dateParts.length >= 6) {
                        const timePart = dateParts[dateParts.length - 2] + ' ' + dateParts[dateParts.length - 1]; // "1:00 PM"
                        const datePart = dateParts.slice(0, dateParts.length - 3).join(' '); // "Friday, January 23, 2026"
                        
                        // Create a Date object from the parsed parts
                        const dateTimeStr = datePart + ' ' + timePart;
                        const dateObj = new Date(dateTimeStr);
                        
                        // Subtract 4 hours (4 * 60 * 60 * 1000 milliseconds)
                        dateObj.setTime(dateObj.getTime() - (4 * 60 * 60 * 1000));
                        
                        // Format back to the same display format
                        const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                        
                        const dayName = weekdays[dateObj.getDay()];
                        const monthName = months[dateObj.getMonth()];
                        const date = dateObj.getDate();
                        const year = dateObj.getFullYear();
                        
                        // Format time
                        let hours = dateObj.getHours();
                        const minutes = dateObj.getMinutes();
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        hours = hours % 12;
                        hours = hours ? hours : 12; // Convert 0 to 12
                        const minutesStr = minutes < 10 ? '0' + minutes : minutes;
                        const timeStr = hours + ':' + minutesStr + ' ' + ampm;
                        
                        adjustedNextExecution = `${dayName}, ${monthName} ${date}, ${year} at ${timeStr}`;
                    }
                } catch (e) {
                    console.error('Error adjusting nextExecution time:', e);
                    // If there's an error, use the original value
                    adjustedNextExecution = schedule.nextExecution;
                }
            }
            
            // Adjust lastRun time by subtracting 4 hours for proper timezone display
            let adjustedLastRun = 'Never';
            if (schedule.lastRun && schedule.lastRun !== 'Never') {
                try {
                    // Parse the ISO date string
                    const dateObj = new Date(schedule.lastRun);
                    
                    // Subtract 4 hours (4 * 60 * 60 * 1000 milliseconds)
                    dateObj.setTime(dateObj.getTime() - (4 * 60 * 60 * 1000));
                    
                    // Format to a friendly display format
                    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                    
                    const dayName = weekdays[dateObj.getDay()];
                    const monthName = months[dateObj.getMonth()];
                    const date = dateObj.getDate();
                    const year = dateObj.getFullYear();
                    
                    // Format time
                    let hours = dateObj.getHours();
                    const minutes = dateObj.getMinutes();
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    hours = hours % 12;
                    hours = hours ? hours : 12; // Convert 0 to 12
                    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
                    const timeStr = hours + ':' + minutesStr + ' ' + ampm;
                    
                    adjustedLastRun = `${dayName}, ${monthName} ${date}, ${year} at ${timeStr}`;
                } catch (e) {
                    console.error('Error adjusting lastRun time:', e);
                    // If there's an error, use the original value
                    adjustedLastRun = schedule.lastRun;
                }
            }
            
            return {
                executionDays: schedule.executionDays || 'None selected',
                timesPerDay: schedule.timesPerDay || '',
                startTime: schedule.workingHours ? schedule.workingHours.split(' - ')[0] : '',
                endTime: schedule.workingHours ? schedule.workingHours.split(' - ')[1] : '',
                interval: schedule.interval || '',
                nextExecution: adjustedNextExecution,
                lastRun: adjustedLastRun
            };
        }
        
        // Handle context data structure
        // Context expects: daysOfWeek, startTime, endTime, timesPerDay
        let selectedDays = 'None selected';
        if (Array.isArray(schedule.daysOfWeek) && schedule.daysOfWeek.length > 0) {
            selectedDays = schedule.daysOfWeek
                .map(day => day.charAt(0).toUpperCase() + day.slice(1))
                .join(', ');
        }
            
        return {
            executionDays: selectedDays,
            timesPerDay: schedule.timesPerDay || '',
            startTime: schedule.startTime || '',
            endTime: schedule.endTime || '',
            interval: schedule.startTime && schedule.endTime && schedule.timesPerDay 
                ? calculateInterval(schedule.startTime, schedule.endTime, schedule.timesPerDay)
                : '',
            nextExecution: '',
            lastRun: 'Never'
        };
    };

    // Calculate interval between executions
    const calculateInterval = (startTime, endTime, timesPerDay) => {
        if (timesPerDay < 2) {
            return 'Invalid configuration';
        }
        
        try {
            const start = new Date(`1970-01-01T${startTime}:00`);
            const end = new Date(`1970-01-01T${endTime}:00`);
            
            // Handle case where end time is next day
            if (end < start) {
                end.setDate(end.getDate() + 1);
            }
            
            const totalMinutes = (end - start) / (1000 * 60);
            const intervalMinutes = totalMinutes / (timesPerDay - 1);
            
            const hours = Math.floor(intervalMinutes / 60);
            const minutes = Math.round(intervalMinutes % 60);
            
            if (hours > 0 && minutes > 0) {
                return `Every ${hours} hour${hours !== 1 ? 's' : ''} and ${minutes} minute${minutes !== 1 ? 's' : ''}`;
            } else if (hours > 0) {
                return `Every ${hours} hour${hours !== 1 ? 's' : ''}`;
            } else {
                return `Every ${minutes} minute${minutes !== 1 ? 's' : ''}`;
            }
        } catch (e) {
            return 'Unable to calculate interval';
        }
    };

    // Update dashboard with current information
    const updateDashboard = () => {
        // Use fetched data if available, otherwise use context data
        const scheduleToUse = scheduleData || schedule;
        const parametersToUse = parametersData || parameters;
        const configuredEmails = getConfiguredEmails();
        const hasConfiguredEmail = configuredEmails.length > 0;

        if (!hasConfiguredEmail) {
            setSystemStatus('Not configured');
            setNextExecution('Not set');
            setLastRun('No records found');
            setExecutionDays('Not set');
            setTimesPerDay('0');
            setWorkingHours('0');
            setIntervalValue('Not set');
            setUserEmail('Not set');
            setAiClassification('Disabled');
            setAiReplyDetection('Disabled');
            setAiReplyGeneration('Disabled');
            return;
        }
        
        // Get schedule information
        const scheduleSummary = getScheduleSummary(scheduleToUse);

        // Update schedule information
        setExecutionDays(scheduleSummary.executionDays || 'Not configured');
        setTimesPerDay(scheduleSummary.timesPerDay || 'Not configured');
        setWorkingHours(
            scheduleSummary.startTime && scheduleSummary.endTime && scheduleSummary.startTime !== 'Loading...' && scheduleSummary.endTime !== 'Loading...'
                ? `${scheduleSummary.startTime} - ${scheduleSummary.endTime}` 
                : 'Not configured'
        );
        setIntervalValue(scheduleSummary.interval || 'Not configured');

        // Check if system should run now
        const shouldRun = shouldRunNow(scheduleToUse);
        setSystemStatus(
            scheduleToUse && (scheduleToUse.daysOfWeek?.length > 0 || scheduleToUse.executionDays) 
                ? (shouldRun ? 'Ready to Run' : 'Scheduled') 
                : 'Not configured'
        );

        // Update email processing information
        setUserEmail(configuredEmails.join(', ') || 'Not set');
        // Handle both API response structure (aiFeatures) and context structure (useAI)
        const aiFeatures = parametersToUse.aiFeatures
            || parametersToUse.useAI
            || parameters.aiFeatures
            || parameters.useAI;
        setAiClassification(aiFeatures?.classification ? 'Enabled' : 'Disabled');
        setAiReplyDetection(aiFeatures?.replyDetection ? 'Enabled' : 'Disabled');
        setAiReplyGeneration(aiFeatures?.replyGeneration ? 'Enabled' : 'Disabled');

        // Update last run information from schedule data if available, otherwise from localStorage
        const lastRunTime = scheduleSummary.lastRun !== 'Never' ? scheduleSummary.lastRun : localStorage.getItem('emailProcessingLastRun');
        setLastRun(lastRunTime || 'No records found');
        
        // Set next execution only if there's a valid schedule
        setNextExecution(
            scheduleToUse && (scheduleToUse.daysOfWeek?.length > 0 || scheduleToUse.executionDays) 
                ? (scheduleSummary.nextExecution || 'Not set')
                : 'Not set'
        );
    };

    // Parse emails from parameters and update available emails
    const updateAvailableEmails = () => {
        const emails = getConfiguredEmails();

        if (emails.length > 0) {
            setAvailableEmails(emails);

            const storedSelectedEmail = localStorage.getItem('emailAutomation_selectedEmail') || '';
            const nextSelectedEmail = emails.includes(selectedEmail)
                ? selectedEmail
                : (emails.includes(storedSelectedEmail) ? storedSelectedEmail : emails[0]);

            if (nextSelectedEmail !== selectedEmail) {
                setSelectedEmail(nextSelectedEmail);
            }
        } else {
            setAvailableEmails([]);
            setSelectedEmail('');
            setScheduleData(null);
            setParametersData(null);
        }
    };

    useEffect(() => {
        if (selectedEmail) {
            localStorage.setItem('emailAutomation_selectedEmail', selectedEmail);
        }
    }, [selectedEmail]);

    // Initialize dashboard and update when context changes
    useEffect(() => {
        updateDashboard();
        updateAvailableEmails();
    }, [schedule, parameters]);

    // Update dashboard when fetched data changes
    useEffect(() => {
        updateDashboard();
    }, [scheduleData, parametersData]);

    // Fetch data for the initially selected email
    useEffect(() => {
        if (selectedEmail) {
            fetchScheduleData(selectedEmail);
            fetchParametersData(selectedEmail);
        }
    }, [selectedEmail]);

    // Show status message
    const showStatus = (message, isSuccess = true) => {
        setStatusBar({
            show: true,
            message: message,
            isSuccess: isSuccess
        });

        // Hide after 5 seconds
        setTimeout(() => {
            setStatusBar(prev => ({ ...prev, show: false }));
        }, 5000);
    };

    const needsEmailConfiguration = getConfiguredEmails().length === 0;

    return (
        <div className="email-automation-container">
            <div className="header-container">
                <h1>Email Automation Dashboard</h1>
                <div className="nav-links">
                    <a href="/#/ai/automations/email" className={`nav-link ${location.pathname === '/ai/automations/email' ? 'active' : ''}`}>Email Dashboard</a>
                    <a href="/#/ai/automations/email/schedule" className={`nav-link ${location.pathname === '/ai/automations/email/schedule' ? 'active' : ''}`}>Schedule Configuration</a>
                    <a href="/#/ai/automations/email/params" className={`nav-link ${location.pathname === '/ai/automations/email/params' ? 'active' : ''}`}>Email Parameters</a>
                </div>
            </div>
            <div className="container">
                {statusBar.show && (
                    <div className={`status-bar ${statusBar.isSuccess ? 'success' : 'error'}`}>
                        <p>{statusBar.message}</p>
                    </div>
                )}

                {needsEmailConfiguration && (
                    <div className="configuration-required" role="status">
                        <h3>Email setup required</h3>
                        <p>Add an email address in Email Parameters before using this automation dashboard.</p>
                        <Button
                            variant="contained"
                            className="configuration-required-btn"
                            onClick={() => navigate('/ai/automations/email/params')}
                        >
                            Open Email Parameters
                        </Button>
                    </div>
                )}

                <div className="dashboard">
                    <div className="card">
                        <h2 className="card-title card-title-with-help">
                            <span className="card-title-text">System Status</span>
                            <button
                                type="button"
                                className={`card-help-btn ${isSystemStatusHelpOpen ? 'active' : ''}`}
                                onClick={() => setIsSystemStatusHelpOpen(prev => !prev)}
                                aria-label={isSystemStatusHelpOpen ? 'Hide System Status help' : 'Show System Status help'}
                                aria-expanded={isSystemStatusHelpOpen}
                                title={isSystemStatusHelpOpen ? 'Hide System Status help' : 'Show System Status help'}
                            >
                                ?
                            </button>
                        </h2>
                        {isSystemStatusHelpOpen && (
                            <div className="card-help-panel">
                                <p>System Status gives a quick read on whether the automation is configured, when it will run next, and whether email processing is currently active.</p>
                                <ul className="card-help-list">
                                    <li><strong>Current Status:</strong> Shows whether the system is "Ready to Run", "Scheduled", or "Not configured".</li>
                                    <li><strong>Next Execution:</strong> Displays the scheduled date and time for the next automated run, adjusted for the America/Puerto_Rico timezone.</li>
                                    <li><strong>Last Run:</strong> Shows when the email processor last executed successfully.</li>
                                    <li><strong>Processing Indicator:</strong> Visual progress bar displayed during active email processing.</li>
                                </ul>
                            </div>
                        )}
                        <div className="info-item">
                            <span className="info-label">Current Status:</span>
                            <span id="systemStatus">{systemStatus}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Next Execution:</span>
                            <span id="nextExecution">{nextExecution}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Last Run:</span>
                            <span id="lastRun">{lastRun}</span>
                        </div>
                        {isProcessing && (
                            <div className="progress-container">
                                <div className="progress-bar">
                                    <div className="progress-fill"></div>
                                </div>
                                <div className="progress-text">Processing...</div>
                            </div>
                        )}
                    </div>

                    <div className="card">
                        <h2 className="card-title card-title-with-help">
                            <span className="card-title-text">Configuration Summary</span>
                            <button
                                type="button"
                                className={`card-help-btn ${isConfigurationSummaryHelpOpen ? 'active' : ''}`}
                                onClick={() => setIsConfigurationSummaryHelpOpen(prev => !prev)}
                                aria-label={isConfigurationSummaryHelpOpen ? 'Hide Configuration Summary help' : 'Show Configuration Summary help'}
                                aria-expanded={isConfigurationSummaryHelpOpen}
                                title={isConfigurationSummaryHelpOpen ? 'Hide Configuration Summary help' : 'Show Configuration Summary help'}
                            >
                                ?
                            </button>
                        </h2>
                        {isConfigurationSummaryHelpOpen && (
                            <div className="card-help-panel">
                                <p>Configuration Summary shows the main scheduling rules that control how often the automation runs and the time range it can use.</p>
                                <ul className="card-help-list">
                                    <li><strong>Execution Days:</strong> Lists the days of the week when the automation is active, such as Monday, Tuesday, and Wednesday.</li>
                                    <li><strong>Times Per Day:</strong> Shows how many times per day the system will process emails.</li>
                                    <li><strong>Working Hours:</strong> Displays the time window, from start to end, during which executions occur.</li>
                                    <li><strong>Interval:</strong> Calculates and displays the time between each execution, such as "Every 2 hours and 30 minutes".</li>
                                </ul>
                            </div>
                        )}
                        <div className="info-item">
                            <span className="info-label">Execution Days:</span>
                            <span id="executionDays">{executionDays}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Times Per Day:</span>
                            <span id="timesPerDay">{timesPerDay}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Working Hours:</span>
                            <span id="workingHours">{workingHours}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Interval:</span>
                            <span id="interval">{interval}</span>
                        </div>
                    </div>

                    <div className="card">
                        <h2 className="card-title card-title-with-help">
                            <span className="card-title-text">Email Processing</span>
                            <button
                                type="button"
                                className={`card-help-btn ${isEmailProcessingHelpOpen ? 'active' : ''}`}
                                onClick={() => setIsEmailProcessingHelpOpen(prev => !prev)}
                                aria-label={isEmailProcessingHelpOpen ? 'Hide Email Processing help' : 'Show Email Processing help'}
                                aria-expanded={isEmailProcessingHelpOpen}
                                title={isEmailProcessingHelpOpen ? 'Hide Email Processing help' : 'Show Email Processing help'}
                            >
                                ?
                            </button>
                        </h2>
                        {isEmailProcessingHelpOpen && (
                            <div className="card-help-panel">
                                <p>Email Processing summarizes which configured inbox is currently selected and whether the AI features used to analyze and draft replies are active.</p>
                                <ul className="card-help-list">
                                    <li><strong>Email Selector:</strong> Dropdown menu to switch between multiple configured email addresses and review multi-email configurations.</li>
                                    <li><strong>AI Classification:</strong> Shows if AI-powered email classification is Enabled or Disabled.</li>
                                    <li><strong>AI Reply Generation:</strong> Indicates whether AI-generated reply functionality is active.</li>
                                </ul>
                            </div>
                        )}
                        {availableEmails.length > 0 && (
                            <div className="info-item">
                                <span className="info-label">Select Email:</span>
                                <select
                                    value={selectedEmail}
                                    onChange={(e) => {
                                        const newEmail = e.target.value;
                                        setSelectedEmail(newEmail);
                                        // Fetch data for the selected email
                                        fetchScheduleData(newEmail);
                                        fetchParametersData(newEmail);
                                    }}
                                    className="email-select"
                                >
                                    {availableEmails.map((email, index) => (
                                        <option key={index} value={email}>{email}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="info-item">
                            <span className="info-label">AI Classification:</span>
                            <span id="aiClassification">{aiClassification}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">AI Reply Generation:</span>
                            <span id="aiReplyGeneration">{aiReplyGeneration}</span>
                        </div>
                    </div>
                </div>

                <div className="instructions">
                    <h3>How to Use This System</h3>
                    <ol>
                        <li><strong>Configure Schedule:</strong> Use the "Schedule Configuration" link to set when the email processor should run</li>
                        <li><strong>Set Parameters:</strong> Use the "Email Parameters" link to configure how emails are processed</li>
                        <li><strong>Automated Execution:</strong> The system will automatically run according to your schedule</li>
                    </ol>
                </div>
            </div>
        </div>
    );
}

export default EmailAutomation;

